import * as React from 'react';
import { useEffect, useState } from 'react';

// Import the new icons
import PaymentIcon from '@mui/icons-material/Payment';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PolicyIcon from '@mui/icons-material/Policy';

import Box from '@mui/material/Box';
import FetchHelper from '../../helpers/fetch_helper';
import Modal from '../modal/modal';
import Anchor from '../shared/anchor';
import ActionWithLoading from '../shared/action_with_loading';
import Actions from '../../actions/actions';
import StepsHelper from '../../helpers/financing_request/steps_helper';
import HelpIdsTable from './help_ids_table';
import { AssignmentInd as AssignmentIndIcon } from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SyncIcon from '@mui/icons-material/Sync';
import PhoneIcon from '@mui/icons-material/SystemSecurityUpdateWarning';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FlagIcon from '@mui/icons-material/Flag';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import SMSHelper from '../../helpers/sms_helper';
import ResurectCustomerModal from './resurect_customer_dialog';
import LocationHelper from '../../helpers/location_helper';
import {
  CustomerHoldCancelModalContent,
  CustomerHoldModalContent,
  ExtendCustomerModal,
} from './super_admin_menu_modals.js';
import moment from 'moment';
import Constants from '../../helpers/constants';
import { useSelector } from 'react-redux';
import BorrowerInfo from './BorrowerInfo';
import FunctionalityAccessHelper from '../../helpers/functionality_access_helper';
import MenuButton from './menu_button';
import BuildIcon from '@mui/icons-material/Build';

export default function SuperAdminMenu() {
  const [user, setUser] = useState(null);
  const [financingRequest, setFinancingRequest] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [financingMethod, setFinancingMethod] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  const userFunctionalityAcls = useSelector((redux) => redux?.userFunctionalityAcls);

  const onLastStep = financingRequest?.steps?.filter((step) => step?.status !== 'accepted')?.length === 1;

  const [showHelpModal, setShowHelpModal] = React.useState(false);
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [showReinstateCustomerModal, setShowReinstateCustomerModal] = React.useState(false);

  const [processing, setProcessing] = React.useState(false);
  const isVisibleForCpf = user?.atype === 'CPF' && !StepsHelper.isPastLastStep(financingRequest);

  const reEvalDocs = async () => {
    await FetchHelper.fetchJson(`financing-requests/${financingRequest.id}/re-eval`, {
      method: 'post',
      body: {},
    });
  };

  const toggleSMSOptin = async () => {
    const isOptIn = customer?.cphone1smsOptIn;
    const confirmButtonText = isOptIn ? 'Opt Out' : 'Opt In';
    Actions.requestModals({
      title: `Are you sure you want to do this?`,
      contents: (
        <div>
          This customer is currently opted <b id={'qa-sms-opt-toggle-current'}>{isOptIn ? 'in to' : 'out of'}</b> SMS.
          <br />
          Performing this action will cancel any existing funding requests out for approval.
        </div>
      ),
      cancelButtonText: 'Go Back',
      okButtonText: confirmButtonText,
      okClass: 'action-primary confirm-cancel action-info',
      modalID: 'qa-sms-opt-toggle-modal',
      okID: 'qa-sms-opt-toggle-confirm',
      cancelID: 'qa-sms-opt-toggle-cancel',
      okProcess: async () => {
        await FetchHelper.fetchJson(`customers/${customer.id}`, {
          method: 'put',
          body: {
            cphone1smsOptIn: !isOptIn,
          },
          version: '/v1',
        });
        await Actions.fetchCustomer(customer.id);
        if (financingRequest) {
          await Actions.fetchFinancingRequest({ customerId: customer.id });
        }
        Actions.setMessages([`Customer has been opted ${!isOptIn ? 'IN to' : 'OUT of'} SMS`]);
      },
      cancelProcess: async () => {},
    });
  };

  const showReinstateDialog = async () => {
    setShowReinstateCustomerModal(true);
  };

  const getDTAToken = () => {
    return financingRequest?.steps
      ?.filter(({ externalSku }) => externalSku === 'CS')?.[0]
      ?.docs?.filter(({ externalSku }) => externalSku === 'DigitalWelcomeCall')?.[0]?.externalDocId;
  };

  const [holdCategorySku, setHoldCategorySku] = useState(customer?.holdCategorySku);
  const [additionalHoldDetails, setAdditionalHoldDetails] = useState('');

  const onHoldSubmit = async (holdSku, additionalDetails) => {
    const action = customer?.holdCategorySku ? 'Remove' : 'Place';
    try {
      if (customer?.holdCategorySku || (holdSku && holdSku !== 'invalid')) {
        await Actions.updateCustomer({
          customerId: customer?.id,
          body: {
            holdCategorySku: customer?.holdCategorySku ? null : holdSku,
            additionalHoldDetails: customer?.holdCategorySku ? null : additionalDetails,
          },
          version: '/v3',
        });
        Actions.setMessages(`${action}d Hold on Customer`);
        return true;
      } else {
        Actions.setErrorMessages(`Please fill out all required fields`);
      }
    } catch (e) {
      Actions.setErrorMessages(`Failed to ${action} Hold on Customer`);
    }
    return false;
  };

  /* eslint-disable  react-hooks/exhaustive-deps */
  useEffect(
    () =>
      Actions.setModalDataEntryOnSubmit(async () => {
        const result = await onHoldSubmit(holdCategorySku, additionalHoldDetails);
        Actions.clearCustomerBannerData();
        if (financingRequest && !holdCategorySku) {
          await reEvalDocs();
          await Actions.fetchEligibility({ customerId: customer?.id });
          await Actions.fetchFinancingRequest({ customerId: customer?.id });
        }
        return result;
      }),
    [holdCategorySku, additionalHoldDetails, customer?.holdCategorySku]
  );
  /* eslint-enable  react-hooks/exhaustive-deps */

  let disabledOptInOptOut =
    customer?.status === 'Cancelled' ||
    !eligibility ||
    SMSHelper.getSMSOverallStatusAccrossSteps(financingRequest?.steps) === 'unsubscribed';
  let buttons = [
    {
      id: 'qa-super-admin-impersonate-button',
      label: 'View as Installer',
      icon: <AssignmentIndIcon />,
      onClick: () => Actions.impersonateCustomerOwner({ customerId: customer.id }),
    },

    {
      id: 'qa-super-admin-help-ids-button',
      label: 'Help IDs',
      icon: <ListAltIcon />,
      onClick: () => setShowHelpModal(true),
    },

    {
      id: 'qa-super-admin-reeval-docs-button',
      label: 'Re-eval Documents',
      icon: <SyncIcon />,
      onClick: async () => {
        await reEvalDocs();
        Actions.setMessages([`Documents re-evaluated`]);
      },
      disabled: !financingRequest,
    },
    {
      id: customer?.cphone1smsOptIn ? 'qa-super-admin-sms-opt-out' : 'qa-super-admin-sms-opt-in',
      label: `SMS opt ${customer?.cphone1smsOptIn ? 'out' : 'in'}`,
      icon: <PhoneIcon />,
      onClick: toggleSMSOptin,
      disabled: disabledOptInOptOut,
    },
    {
      id: 'qa-super-admin-cancel-docs-button',
      label: 'Cancel Loan Documents',
      icon: <DoDisturbIcon color="inherit" />,
      onClick: () => setShowCancelModal(true),
      disabled: !isVisibleForCpf || !financingRequest,
    },
    {
      id: 'qa-super-admin-customer-hold-button',
      label: customer?.holdCategorySku ? 'Remove Customer Hold' : 'Place Customer on Hold',
      icon: <FlagIcon color="inherit" />,
      onClick: async () => {
        return Actions.requestModals({
          title: customer?.holdCategorySku ? 'Remove Customer Hold' : 'Place Customer on Hold',
          contents: customer?.holdCategorySku ? (
            <CustomerHoldCancelModalContent />
          ) : (
            <CustomerHoldModalContent
              setHoldCategorySku={setHoldCategorySku}
              setAdditionalHoldDetails={setAdditionalHoldDetails}
            />
          ),
          cancelButtonText: 'Go Back',
          okButtonText: customer?.holdCategorySku ? 'Yes, Remove Hold' : 'Yes, Place Hold',
          okClass: 'action-warning',
          modalID: 'qa-hold-toggle-modal',
          okID: 'qa-hold-toggle-confirm',
          cancelID: 'qa-hold-toggle-cancel',
          waitForSubmitResultToClose: true,
          okProcess: async () => await Actions.modalDataEntryOnSubmit(),
          cancelProcess: async () => {},
        });
      },
      disabled: customer?.status === 'Cancelled' || !customer?.currentCreditCheckId,
    },
    {
      id: 'qa-super-admin-extend-customer-button',
      label: 'Extend Job Expiration',
      icon: <MoreTimeIcon />,
      onClick: async () => {
        await Actions.requestModals({
          title: `Are you sure you want to extend this customer?`,
          contents: <ExtendCustomerModal />,
          cancelButtonText: 'Go Back',
          okButtonText: 'Yes, Extend',
          okClass: 'action-primary',
          modalID: 'qa-extend-customer-modal',
          okID: 'qa-extend-customer-confirm',
          cancelID: 'qa-extend-customer-cancel',
          okProcess: async () => {
            const result = await Actions.modalDataEntryOnSubmit();
            await reEvalDocs();
            return result;
          },
          waitForSubmitResultToClose: true,
          cancelProcess: async () => {},
        });
      },
      disabled:
        !onLastStep ||
        !FunctionalityAccessHelper.hasACL(userFunctionalityAcls, Constants.FUNCTIONALITY_ACL_JOB_EXTENSION),
    },
    {
      id: 'qa-super-admin-reinstate-customer-button',
      label: !customer?.currentCreditCheckId
        ? `Reinstate Customer (${customer?.creditCheckStatus === 'Cancelled' ? 'credit cancelled' : 'no credit'})`
        : 'Reinstate Customer',
      icon: <RocketLaunchIcon />,
      onClick: showReinstateDialog,
      disabled: customer?.status !== 'Cancelled' || !customer?.currentCreditCheckId,
    },
    {
      id: 'qa-super-admin-verify-borrower-id',
      label: 'Verify Borrower ID',
      icon: <ContactEmergencyIcon />,
      onClick: () => {
        Actions.requestModals({
          title: `Borrower Info`,
          contents: <BorrowerInfo customer={customer} customerId={customer?.id} />,
          okProcess: async () => {},
          cancelProcess: async () => {},
          okStyle: {
            display: 'none',
          },
          modalClass: 'borrower-info-modal',
          cancelStyle: {},
          cancelButtonText: 'close window',
          customer: customer,
        });
      },
      disabled: !userFunctionalityAcls?.some(
        (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_RETRIEVE_BORROWER_INFO
      ),
    },
  ];

  const goToButtons = [
    {
      id: 'qa-super-admin-go-to-uw-dash',
      label: 'UW Dashboard',
      icon: <PolicyIcon />, // Better represents a dashboard
      onClick: () => Actions.goToDecisionDetails({ id: eligibility?.creditCheck?.externalId, inNewTab: true }),
      disabled: !eligibility?.creditCheck?.externalId,
    },
    {
      id: 'qa-super-admin-go-to-dta',
      label: 'DTA',
      icon: <EditNoteIcon />, // Better for decision/assessment tools
      onClick: () => Actions.goToWelcomeCall({ id: getDTAToken(), inNewTab: true }),
      disabled:
        !financingRequest ||
        (financingMethod?.lenderCode || '').toLowerCase().includes('mosaic') ||
        (financingMethod?.lenderCode || '').toLowerCase().includes('enerbank') ||
        !getDTAToken(),
    },
    {
      id: 'qa-super-admin-go-to-autopay',
      label: 'Autopay Preferences',
      icon: <PaymentIcon />, // Perfect for payment/autopay functionality
      onClick: () => {
        Actions.fetchAutoPay({ customerId: customer?.id }).then((autopay) => {
          window
            .open(
              `${LocationHelper.getUrlForSubdomain('switch')}/auto-pay/${autopay?.token}/preferences?role=CPF`,
              '_blank'
            )
            .focus();
        });
      },
      disabled:
        customer?.status === 'Cancelled' ||
        !customer?.currentCreditCheckId ||
        !financingRequest ||
        (financingMethod?.lenderCode || '').toLowerCase().includes('mosaic') ||
        (financingMethod?.lenderCode || '').toLowerCase().includes('enerbank'),
    },
    {
      id: 'qa-super-admin-go-to-rtdr',
      label: 'RTDR',
      icon: <DocumentScannerIcon />, // Better represents document review
      onClick: () => {
        window
          .open(
            `${LocationHelper.getUrlForSubdomain('switch')}/admin/customers/${customer?.id}/document-review`,
            '_blank'
          )
          .focus();
      },
      disabled: !financingRequest,
    },
    {
      id: 'qa-super-admin-go-to-owner-admin',
      label: 'Owner Admin',
      icon: <PersonIcon />, // Better represents individual user/owner
      onClick: () => {
        window
          .open(`${LocationHelper.getUrlForSubdomain('switch')}/account/users/${customer?.userId}`, '_blank')
          .focus();
      },
    },
    {
      id: 'qa-super-admin-go-to-company-admin', // Fixed duplicate ID
      label: 'Company Admin',
      icon: <BusinessIcon />, // Perfect for company/business administration
      onClick: () => {
        window
          .open(
            `${LocationHelper.getUrlForSubdomain('switch')}/account/companies/${
              customer?.owner?.company?.coid
            }/companyInfo`,
            '_blank'
          )
          .focus();
      },
      disabled: !customer?.owner?.company?.coid,
    },
    {
      id: 'qa-super-admin-customer-history-button',
      label: 'Customer History',
      icon: <AccessAlarmIcon />,
      onClick: () => {
        window.open(LocationHelper.getUrlForSubdomain('switch') + `/customers/${customer.id}/history`, '_blank');
        // Actions.setView({ page: 'customerHistory', id: customer.id });
        // Actions.setRoute(`/customers/${customer.id}/history`);
      },
    },
  ];

  const expirationDate = moment().add(30, 'days').format('MM/DD/YYYY');
  return (
    <Box display="flex" justifyContent="center" marginTop="10px" marginBottom="5px">
      <MenuButton
        onOpen={() => {
          (async () => {
            const data = await Actions.getSuperAdminToolsData();
            setUser(data?.user);
            setFinancingRequest(data?.financingRequest);
            setCustomer(data?.customer);
            setProposal(data?.proposal);
            setFinancingMethod(data?.financingMethod);
            setEligibility(data?.eligibility);
          })();
        }}
        name={'Go To'}
        clazzName={'super-admin-goto'}
        buttons={goToButtons}
        icon={<ExitToAppIcon />}
      />
      <div style={{ padding: '10px' }}></div>
      <MenuButton
        onOpen={() => {
          (async () => {
            const data = await Actions.getSuperAdminToolsData();
            setUser(data?.user);
            setFinancingRequest(data?.financingRequest);
            setCustomer(data?.customer);
            setProposal(data?.proposal);
            setFinancingMethod(data?.financingMethod);
            setEligibility(data?.eligibility);
          })();
        }}
        name={'Super Admin Tools'}
        clazzName={'super-admin-menu'}
        buttons={buttons}
        icon={<BuildIcon />}
      />
      {showHelpModal && (
        <Modal id="qa-help-id-modal" container={document.body} customer={{}} hideHeader minWidth="500px">
          <HelpIdsTable
            eligibility={eligibility}
            financingRequest={financingRequest}
            customer={customer}
            financingMethod={financingMethod}
            proposal={proposal}
            onCloseClick={() => setShowHelpModal(false)}
          />
        </Modal>
      )}
      {showCancelModal && (
        <Modal
          container={document.body}
          customer={customer}
          title={"Super Admin, are you sure you want to cancel this customer's docs?"}
        >
          {' '}
          <div className="confirmation-section-wrapper" id={'cpf-cancel-docs-modal'}>
            <div className="confirmation-section-message">
              <p>
                <span>
                  Are you sure you want to cancel these documents? If signed, the customer will need to re-sign.
                </span>
                <br />
                <span>
                  This customer will now expire on {<span id="qa-cpf-cancel-docs-expiredate">{expirationDate}</span>}.
                  Contract Signing must be completed by {expirationDate} or this customer will automatically be
                  cancelled and any disbursed funds will be clawed back.
                </span>
              </p>
            </div>
            <div className="confirmation-section-controls" style={{ display: 'flex' }}>
              <Anchor
                className="do-not-cancel"
                id={'do-not-cpf-cancel-docs'}
                onClick={() => {
                  setShowCancelModal(false);
                }}
              >
                Don't cancel them
              </Anchor>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <ActionWithLoading
                onClick={async () => {
                  setProcessing(true);
                  await Actions.deleteFinancingRequest({
                    customerId: customer?.id,
                    doNotNotifyEnsemble: true,
                    doNotCancelCustomer: true,
                    cancellationReason: `CPF ${user.username} making changes`,
                  });
                  await Actions.fetchProposal(proposal?.id);
                  Actions.goToProposalPage({ subPage: 'eligibilityStatus' });
                  setShowCancelModal(false);
                }}
                classNames="confirm-cancel action-warning"
                id={'confirm-cpf-cancel-docs'}
                processing={processing}
              >
                Yes, cancel them
              </ActionWithLoading>
            </div>
          </div>
        </Modal>
      )}
      {showReinstateCustomerModal && (
        <ResurectCustomerModal
          setProcessing={setProcessing}
          processing={processing}
          hideFunction={() => setShowReinstateCustomerModal(false)}
        />
      )}
    </Box>
  );
}
